/**
 * @file usePersistedState.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-14
 * @modified 2026-10-01
 *
 * Drop-in replacement for React's useState that survives navigating away
 * and back to a component. State is keyed by a unique string id and stored
 * in the nav store (tab-local sessionStorage).
 *
 *     const [filters, setFilters] = usePersistedState(
 *       'searchContacts.filters',
 *       { search: '', type: 'all' }
 *     );
 *
 * Opt-out:
 *
 *     const [draft, setDraft] = usePersistedState(
 *       'createInvoice.draft',
 *       initial,
 *       { autoReset: true }
 *     );
 *
 *   `autoReset: true` makes the hook behave like a plain useState — initial
 *   value is used on every mount, and the slot in the store is wiped on
 *   mount so future renavigations also start clean. Use this for pages that
 *   should always open fresh (e.g. wizards, "Create X" forms).
 *
 *     const [filters, setFilters] = usePersistedState(
 *       'formulations.filters',
 *       DEFAULT_FILTERS,
 *       { merge: true }
 *     );
 *
 *   `merge: true` layers a stored plain object over the initial one instead of
 *   replacing it. Use it for any object-shaped slot whose keys may change
 *   between releases — a filter set, a column config. Without it, a slot
 *   written before a key existed comes back missing that key, and the
 *   component reads undefined off a shape it believes it controls: renaming
 *   the formulations filter from salesAccounts to productTypes left every
 *   tab that had visited the page holding the old object, and the next search
 *   threw on `filters.productTypes.length`. Off by default, because a caller
 *   that deliberately stores a partial object should keep getting it back.
 *
 *     const reveal = useReveal('search-invoices');
 *     const [filters, setFilters] = usePersistedState(
 *       'searchInvoices.filters',
 *       DEFAULT_FILTERS,
 *       { merge: true, seed: revealFilters(reveal) }
 *     );
 *
 *   `seed` is laid over the starting value (after `merge`): merged in when
 *   both are plain objects, otherwise it replaces it. null / undefined means
 *   no seed. It is for a screen that arrives with something already decided —
 *   a "Find in …" handoff (utils/revealInSearch.js) puts its term in the box
 *   and its page back to 1 this way. It has to be the FIRST render's value:
 *   applied by a setter in a mount effect, the mount search has already run
 *   on the render before it (which is exactly the bug this replaced). A seeded
 *   value is written back to the store on mount, since unlike a plain restore
 *   it differs from what is stored. Seed wins over `autoReset`.
 *
 * Notes:
 *  - Keys SHOULD be namespaced by page (`searchContacts.filters`,
 *    `searchPOs.search`, etc.) so two unrelated components can't collide.
 *  - State writes go through Zustand and are throttled by React's normal
 *    batching. There's no debounce — writes are cheap.
 *  - Don't store huge data (full result sets, file blobs) here. Keep it to
 *    filter inputs, open-modal-id, expanded-row-ids and friends.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavStore } from '../store/navStore';

/** Objects only — an array or a Date must be replaced wholesale, not merged. */
const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v) && v.constructor === Object;

export default function usePersistedState(key, initial, options = {}) {
  const { autoReset = false, merge = false, seed } = options;
  const seededRef = useRef(false);

  // Resolve the starting value once on mount. If autoReset is on we ignore
  // whatever's in the store and start from `initial`.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const stored = useNavStore.getState().pages[key];
    if (autoReset || stored === undefined) {
      initialRef.current = initial;
    } else if (merge && isPlainObject(stored) && isPlainObject(initial)) {
      // A slot written before a key was added or renamed comes back missing it,
      // and the component then reads undefined off a shape it believes it
      // controls. Renaming the formulations filter from salesAccounts to
      // productTypes did exactly that: every tab that had visited the page
      // still held the old object, and `filters.productTypes.length` threw on
      // the next search. Merging over the defaults means a new key always
      // exists, and a stale one is harmless.
      initialRef.current = { ...initial, ...stored };
    } else {
      initialRef.current = stored;
    }
    if (seed !== undefined && seed !== null) {
      initialRef.current = isPlainObject(seed) && isPlainObject(initialRef.current)
        ? { ...initialRef.current, ...seed }
        : seed;
      seededRef.current = true;
    }
  }

  const [value, setValue] = useState(initialRef.current);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      if (autoReset) {
        // Wipe the slot so any future renavigation also starts fresh.
        useNavStore.getState().clearPage(key);
      }
      // A seeded value is not what the store holds, so say so now — or
      // leaving before anything else changes would come back to the old one.
      if (seededRef.current) {
        useNavStore.getState().setPage(key, value);
      }
      return;
    }
    // Sync changes back to the store. Skipping the first effect avoids
    // writing the same value we just read back.
    useNavStore.getState().setPage(key, value);
  }, [value, key, autoReset]);

  return [value, setValue];
}
